import { createContext, useCallback, useContext, useMemo, type Dispatch } from 'react';

import type { Volume } from '~/shared/types/api';

import { filterVolumes } from '../lib/filterVolumes';
import {
  defaultExplorationUrlSnapshot,
  normalizeExplorationUrlSnapshot,
  type ExplorationUrlSnapshot,
} from '../lib/explorationUrlParams';
import { appendFilter } from '../lib/sidebar/addFilter';
import { migrateStateOnViewSwitch } from '../lib/migrateStateOnViewSwitch';
import {
  drillLocationsEqual,
  emptyDrillLocation,
  isDrillLocationEmpty,
  popDrillLocation,
} from '../lib/drill/drillLocation';
import {
  drillHistoryAfterClearingVolumeSelection,
  drillHistoryAfterUrlSync,
  filterNavHistory,
  filtersEqual,
  locationForSelection,
  selectionNavUpdate,
} from './explorationNavigation';

export { drillHistoryAfterUrlSync } from './explorationNavigation';
import type {
  ExplorationAccountCache,
  ExplorationFilter,
  ExplorationHighlight,
  ExplorationSecondary,
  ExplorationSelection,
  ExplorationState,
  ExplorationVolume,
  DrillLocation,
  ViewMode,
  DisplayMode,
  CoverageGrouping,
  LoadingProgress,
} from '../types/exploration';

const emptyVolumes: ExplorationVolume[] = [];

export type ExplorationAction =
  | { type: 'setAccount'; accountId: string | null }
  | { type: 'setView'; view: ViewMode }
  | { type: 'setDisplayMode'; displayMode: DisplayMode }
  | { type: 'setCoverageGrouping'; coverageGrouping: CoverageGrouping }
  | { type: 'drillFromCoverage'; location: DrillLocation }
  | { type: 'setSecondary'; secondary: ExplorationSecondary }
  | { type: 'setTagKey'; tagKey: string }
  | { type: 'setDrillPath'; location: DrillLocation; historyMode?: 'push' | 'replace' }
  | { type: 'drillUp' }
  | { type: 'setSearch'; searchQuery: string }
  | { type: 'setFilters'; filters: ExplorationFilter[] }
  | { type: 'addFilter'; filter: ExplorationFilter }
  | { type: 'setGroupByAccount'; groupByAccount: boolean; preserveDrillPath?: boolean }
  | { type: 'expandOverflow'; parentKey: string }
  | { type: 'setSelection'; selectedNode: ExplorationSelection }
  | {
      type: 'selectVolume';
      volume: ExplorationVolume;
      /** URL hydration must not push a visit — the URL already represents this selection. */
      historyMode?: 'push' | 'none';
    }
  | { type: 'selectInstance'; instanceId: string; vols: ExplorationVolume[] }
  | { type: 'clearSelection' }
  | { type: 'toggleSidebar' }
  | { type: 'setSidebarCollapsed'; collapsed: boolean }
  | { type: 'setHighlight'; highlight: ExplorationHighlight }
  | { type: 'resetViewState' }
  | { type: 'syncFromUrl'; snapshot: ExplorationUrlSnapshot }
  | { type: 'clearUrlHistoryWrite' };

export const initialExplorationState: ExplorationState = {
  accountId: null,
  view: 'exploration',
  displayMode: 'bubbles',
  secondary: 'none',
  coverageGrouping: 'hierarchy',
  tagKey: '',
  drillPath: emptyDrillLocation(),
  drillHistory: [],
  urlHistoryWrite: null,
  selectedNode: null,
  searchQuery: '',
  filters: [],
  groupByAccount: false,
  overflowPages: {},
  sidebarCollapsed: false,
  highlight: null,
};

export function explorationReducer(
  state: ExplorationState,
  action: ExplorationAction
): ExplorationState {
  switch (action.type) {
    case 'setAccount': {
      const isAccountChange = state.accountId != null && state.accountId !== action.accountId;
      return {
        ...state,
        accountId: action.accountId,
        drillPath: isAccountChange ? emptyDrillLocation() : state.drillPath,
        drillHistory: isAccountChange ? [] : state.drillHistory,
        selectedNode: null,
        filters: isAccountChange ? [] : state.filters,
        overflowPages: {},
      };
    }
    case 'setView': {
      return { ...state, ...migrateStateOnViewSwitch(state, action.view), drillHistory: [] };
    }
    case 'setDisplayMode': {
      const displayMode = normalizeExplorationUrlSnapshot({
        ...defaultExplorationUrlSnapshot,
        displayMode: action.displayMode,
      }).displayMode;
      return { ...state, displayMode, drillHistory: [] };
    }
    case 'setCoverageGrouping':
      return { ...state, coverageGrouping: action.coverageGrouping, drillHistory: [] };
    case 'drillFromCoverage': {
      return {
        ...state,
        view: 'exploration',
        drillPath: action.location,
        drillHistory: [],
        overflowPages: {},
      };
    }
    case 'setSecondary':
      return {
        ...state,
        secondary: action.secondary,
        drillPath: emptyDrillLocation(),
        drillHistory: [],
        overflowPages: {},
      };
    case 'setTagKey': {
      const tagUnchanged = action.tagKey === state.tagKey;
      return {
        ...state,
        tagKey: action.tagKey,
        drillPath: tagUnchanged ? state.drillPath : emptyDrillLocation(),
        drillHistory: tagUnchanged ? state.drillHistory : [],
        overflowPages: tagUnchanged ? state.overflowPages : {},
      };
    }
    case 'setDrillPath': {
      if (drillLocationsEqual(state.drillPath, action.location)) {
        return state;
      }
      const historyMode = action.historyMode ?? 'replace';
      return {
        ...state,
        drillPath: action.location,
        drillHistory:
          historyMode === 'push' ? [...state.drillHistory, state.drillPath] : [],
        urlHistoryWrite: historyMode === 'push' ? 'push' : 'replace',
        overflowPages: {},
        selectedNode: null,
      };
    }
    case 'drillUp': {
      // Canvas out with a non-empty visit stack uses navigate(-1) instead, so
      // syncFromUrl restores the full prior URL (filters included). This
      // branch is the peel fallback for shared links / empty stack, and for
      // any caller that still dispatches drillUp directly.
      if (state.drillHistory.length > 0) {
        const prev = state.drillHistory[state.drillHistory.length - 1]!;
        return {
          ...state,
          drillPath: prev,
          drillHistory: state.drillHistory.slice(0, -1),
          selectedNode: null,
          overflowPages: {},
          urlHistoryWrite: 'replace',
        };
      }
      // No visit to restore (e.g. a URL-hydrated selection): clear the
      // selection first and stay on the same group/instance — only peel the
      // structural facet once nothing is selected.
      if (state.selectedNode != null) {
        return { ...state, selectedNode: null, overflowPages: {}, urlHistoryWrite: 'replace' };
      }
      if (isDrillLocationEmpty(state.drillPath)) {
        return state;
      }
      const peeled = popDrillLocation(state.drillPath);
      // Empty visit stack (shared link / breadcrumb replace): peel one hierarchy facet.
      return {
        ...state,
        drillPath: peeled,
        selectedNode: null,
        overflowPages: {},
        urlHistoryWrite: 'push',
      };
    }
    case 'setSearch':
      return { ...state, searchQuery: action.searchQuery };
    case 'setFilters': {
      if (filtersEqual(state.filters, action.filters)) {
        return state;
      }
      const clearing = action.filters.length === 0;
      return {
        ...state,
        filters: action.filters,
        // Clearing must not push a phantom undo frame (caused extra navigate(-1) no-ops).
        drillHistory: filterNavHistory(state, action.filters),
        urlHistoryWrite: clearing ? 'replace' : 'push',
      };
    }
    case 'addFilter': {
      const nextFilters = appendFilter(state.filters, action.filter);
      if (nextFilters === state.filters) {
        return state;
      }
      return {
        ...state,
        filters: nextFilters,
        drillHistory: filterNavHistory(state, nextFilters),
        urlHistoryWrite: 'push',
      };
    }
    case 'setGroupByAccount':
      return {
        ...state,
        groupByAccount: action.groupByAccount,
        drillPath: action.preserveDrillPath ? state.drillPath : emptyDrillLocation(),
        drillHistory: action.preserveDrillPath ? state.drillHistory : [],
        overflowPages: action.preserveDrillPath ? state.overflowPages : {},
      };
    case 'expandOverflow':
      return {
        ...state,
        overflowPages: {
          ...state.overflowPages,
          [action.parentKey]: (state.overflowPages[action.parentKey] ?? 0) + 1,
        },
      };
    case 'setSelection': {
      if (
        action.selectedNode?.kind === 'volume' &&
        state.selectedNode?.kind === 'volume' &&
        state.selectedNode.volume.ebsId === action.selectedNode.volume.ebsId
      ) {
        return state;
      }
      if (action.selectedNode == null) {
        return {
          ...state,
          selectedNode: null,
          drillHistory: drillHistoryAfterClearingVolumeSelection(state),
          sidebarCollapsed: false,
          urlHistoryWrite: 'replace',
          highlight: null,
        };
      }
      const nav = selectionNavUpdate(state, locationForSelection(state, action.selectedNode), {
        pushWhenLocationUnchanged: action.selectedNode.kind === 'volume',
      });
      return {
        ...state,
        ...nav,
        selectedNode: action.selectedNode,
        sidebarCollapsed: false,
        highlight: null,
      };
    }
    case 'selectVolume': {
      if (
        state.selectedNode?.kind === 'volume' &&
        state.selectedNode.volume.ebsId === action.volume.ebsId
      ) {
        return state;
      }
      const selection: ExplorationSelection = { kind: 'volume', volume: action.volume };
      if (action.historyMode === 'none') {
        // URL-hydrated selection: reconcile against the volume's own canonical
        // location (still instance-scoped only if the URL's drillPath already
        // named this volume's instance) instead of trusting drillPath verbatim —
        // otherwise a stale/mismatched instance segment renders an empty group
        // with no bubble for this volume. Only write the URL when that
        // reconciliation actually changed something — the common case (e.g.
        // stepping Back through several same-group volume selections) needs
        // no correction, and forcing a write there re-pushes a duplicate URL
        // that fights the browser's own in-flight Back navigation, turning a
        // single Back press into two (volume deselects to the group first).
        const resolved = locationForSelection(state, selection) ?? state.drillPath;
        const isCorrection = !drillLocationsEqual(resolved, state.drillPath);
        return {
          ...state,
          drillPath: resolved,
          selectedNode: selection,
          sidebarCollapsed: false,
          urlHistoryWrite: isCorrection ? 'replace' : 'none',
          overflowPages: isCorrection ? {} : state.overflowPages,
          highlight: null,
        };
      }
      const nav = selectionNavUpdate(state, locationForSelection(state, selection), {
        pushWhenLocationUnchanged: true,
      });
      return {
        ...state,
        ...nav,
        selectedNode: selection,
        sidebarCollapsed: false,
        highlight: null,
      };
    }
    case 'selectInstance': {
      const anchor = action.vols.find((v) => v.instanceId === action.instanceId);
      if (!anchor) {
        return state;
      }
      if (
        state.selectedNode?.kind === 'instance' &&
        state.selectedNode.instanceId === action.instanceId
      ) {
        return state;
      }
      const selection: ExplorationSelection = {
        kind: 'instance',
        instanceId: action.instanceId,
        vols: action.vols,
      };
      const nextLocation = locationForSelection(state, selection);
      // Already at this instance: sidebar/highlight only — do not push/replace URL
      // (keeps volume= on the current history entry for volume undo).
      if (nextLocation != null && drillLocationsEqual(nextLocation, state.drillPath)) {
        return {
          ...state,
          selectedNode: selection,
          sidebarCollapsed: false,
          urlHistoryWrite: 'none',
          highlight: null,
        };
      }
      const nav = selectionNavUpdate(state, nextLocation);
      return {
        ...state,
        ...nav,
        selectedNode: selection,
        sidebarCollapsed: false,
        highlight: null,
      };
    }
    case 'clearSelection':
      return {
        ...state,
        selectedNode: null,
        drillHistory: drillHistoryAfterClearingVolumeSelection(state),
        urlHistoryWrite: 'replace',
        highlight: null,
      };
    case 'toggleSidebar':
      return { ...state, sidebarCollapsed: !state.sidebarCollapsed };
    case 'setSidebarCollapsed':
      return { ...state, sidebarCollapsed: action.collapsed };
    case 'setHighlight':
      return { ...state, highlight: action.highlight };
    case 'resetViewState':
      return {
        ...state,
        drillPath: emptyDrillLocation(),
        drillHistory: [],
        selectedNode: null,
        filters: [],
        overflowPages: {},
        highlight: null,
      };
    case 'syncFromUrl': {
      const snapshot = normalizeExplorationUrlSnapshot(action.snapshot);
      const nextHistory = drillHistoryAfterUrlSync(
        state.drillHistory,
        state.drillPath,
        snapshot.drillPath
      );
      return {
        ...state,
        accountId: snapshot.accountId ?? state.accountId,
        view: snapshot.view,
        displayMode: snapshot.displayMode,
        secondary: snapshot.secondary,
        coverageGrouping: snapshot.coverageGrouping,
        tagKey: snapshot.tagKey,
        drillPath: snapshot.drillPath,
        drillHistory: nextHistory,
        urlHistoryWrite: null,
        searchQuery: snapshot.searchQuery,
        filters: snapshot.filters,
        groupByAccount: snapshot.groupByAccount,
        selectedNode: null,
        overflowPages: {},
        highlight: null,
      };
    }
    case 'clearUrlHistoryWrite':
      return state.urlHistoryWrite == null ? state : { ...state, urlHistoryWrite: null };
    default:
      return state;
  }
}

export interface ExplorationDataContextValue {
  accounts: Record<string, ExplorationAccountCache>;
  apiVolumeByEbsId: Record<string, Volume>;
  /** True during auth wait, initial volume fetch, or background refetch. */
  loading: boolean;
  /** True only before first successful volume load — used to hide sidebar on first paint. */
  initialLoading: boolean;
  loadingProgress: LoadingProgress | null;
  error: string | null;
  refresh: () => void;
}

export const ExplorationStateContext = createContext<ExplorationState | null>(null);
export const ExplorationDispatchContext =
  createContext<Dispatch<ExplorationAction> | null>(null);
export const ExplorationDataContext =
  createContext<ExplorationDataContextValue | null>(null);

export function useExplorationState() {
  const ctx = useContext(ExplorationStateContext);
  if (!ctx)
    throw new Error(
      'useExplorationState must be used within ExplorationRootProvider'
    );
  return ctx;
}

export function useExplorationDispatch() {
  const ctx = useContext(ExplorationDispatchContext);
  if (!ctx)
    throw new Error(
      'useExplorationDispatch must be used within ExplorationRootProvider'
    );
  return ctx;
}

export function useExplorationData() {
  const ctx = useContext(ExplorationDataContext);
  if (!ctx)
    throw new Error('useExplorationData must be used within ExplorationRootProvider');
  return ctx;
}

export function useExplorationActions() {
  const dispatch = useExplorationDispatch();
  return useMemo(
    () => ({
      setAccount: (accountId: string | null) => dispatch({ type: 'setAccount', accountId }),
      setView: (view: ViewMode) => dispatch({ type: 'setView', view }),
      setDisplayMode: (displayMode: DisplayMode) =>
        dispatch({ type: 'setDisplayMode', displayMode }),
      setCoverageGrouping: (coverageGrouping: CoverageGrouping) =>
        dispatch({ type: 'setCoverageGrouping', coverageGrouping }),
      drillFromCoverage: (location: DrillLocation) =>
        dispatch({ type: 'drillFromCoverage', location }),
      setSecondary: (secondary: ExplorationSecondary) =>
        dispatch({ type: 'setSecondary', secondary }),
      setTagKey: (tagKey: string) => dispatch({ type: 'setTagKey', tagKey }),
      setDrillPath: (
        location: DrillLocation,
        opts?: { historyMode?: 'push' | 'replace' }
      ) =>
        dispatch({
          type: 'setDrillPath',
          location,
          historyMode: opts?.historyMode ?? 'replace',
        }),
      drillUp: () => dispatch({ type: 'drillUp' }),
      setSearch: (searchQuery: string) => dispatch({ type: 'setSearch', searchQuery }),
      setFilters: (filters: ExplorationFilter[]) =>
        dispatch({ type: 'setFilters', filters }),
      addFilter: (filter: ExplorationFilter) => dispatch({ type: 'addFilter', filter }),
      setGroupByAccount: (groupByAccount: boolean) =>
        dispatch({ type: 'setGroupByAccount', groupByAccount }),
      expandOverflow: (parentKey: string) => dispatch({ type: 'expandOverflow', parentKey }),
      selectVolume: (volume: ExplorationVolume) =>
        dispatch({ type: 'selectVolume', volume }),
      selectInstance: (
        instanceId: string,
        vols: import('../types/exploration').ExplorationVolume[]
      ) => dispatch({ type: 'selectInstance', instanceId, vols }),
      clearSelection: () => dispatch({ type: 'clearSelection' }),
      toggleSidebar: () => dispatch({ type: 'toggleSidebar' }),
      setSidebarCollapsed: (collapsed: boolean) =>
        dispatch({ type: 'setSidebarCollapsed', collapsed }),
      setHighlight: (highlight: ExplorationHighlight) =>
        dispatch({ type: 'setHighlight', highlight }),
    }),
    [dispatch]
  );
}

export function useCurrentAccountVolumes() {
  const state = useExplorationState();
  const { accounts } = useExplorationData();
  if (!state.accountId) return emptyVolumes;
  return accounts[state.accountId]?.volumes ?? emptyVolumes;
}

export function useFilteredVolumes() {
  const volumes = useCurrentAccountVolumes();
  const state = useExplorationState();
  return useMemo(
    () => filterVolumes(volumes, state.filters, state.searchQuery),
    [volumes, state.filters, state.searchQuery]
  );
}
